"use client";

import type { ReactNode } from "react";
import { formatTooltipLabel } from "@/lib/app-detail/chart-utils";
import { useTooltipSurface } from "@/components/ui/tooltip";

export type ChartTooltipRow = {
	key: string;
	/** The line's colour, drawn as the row's dot so the row reads against the legend. */
	color?: string;
	name: ReactNode;
	value: ReactNode;
};

/**
 * A chart's hover card on the same surface as every other tooltip in the app (useTooltipSurface), with a date
 * header and one row per line, so every multi-line chart hovers the same way.
 */
export function ChartTooltipCard({ label, rows, footer }: { label?: string; rows: ChartTooltipRow[]; footer?: ReactNode }) {
	const { className } = useTooltipSurface();
	if (rows.length === 0 && !footer) return null;
	return (
		<div className={className}>
			{label && <div className="mb-1 opacity-70">{formatTooltipLabel(label)}</div>}
			{rows.map((r) => (
				<div key={r.key} className="flex items-center justify-between gap-3 py-0.5">
					<span className="flex min-w-0 items-center gap-1.5">
						{r.color && <span aria-hidden className="inline-block h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: r.color }} />}
						{r.name}
					</span>
					<span className="font-medium tabular-nums">{r.value}</span>
				</div>
			))}
			{footer && <div className="mt-1 opacity-70">{footer}</div>}
		</div>
	);
}

/** One legend entry per line, in the line's colour; wraps on a phone. */
export function ChartSeriesLegend({ items, className }: { items: { key: string; color: string; label: ReactNode }[]; className?: string }) {
	if (items.length === 0) return null;
	return (
		<ul aria-label="Legend" className={`flex flex-wrap gap-x-3 gap-y-1 text-xs ${className ?? ""}`}>
			{items.map((it) => (
				<li key={it.key} className="flex items-center gap-1.5" style={{ color: it.color }}>
					<span aria-hidden className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: it.color }} />
					<span className="text-foreground">{it.label}</span>
				</li>
			))}
		</ul>
	);
}
